import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { DerivedMetricKey } from "../data/types";
import { METRIC_DEFINITIONS } from "../data/methodology";

interface MetricDefinitionDrawerProps {
  open: boolean;
  metricKey: DerivedMetricKey | null;
  onClose: () => void;
}

/** Slide-in panel explaining a single derived indicator in plain English. */
export const MetricDefinitionDrawer: React.FC<MetricDefinitionDrawerProps> = ({
  open,
  metricKey,
  onClose,
}) => {
  const theme = useTheme();
  const def = metricKey ? METRIC_DEFINITIONS[metricKey] : null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 320, sm: 420 }, p: 3 }} role="dialog" aria-label="Metric definition">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            {def ? `${def.code} — ${def.name}` : "Metric"}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close definition">
            <CloseIcon />
          </IconButton>
        </Stack>

        {def && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="primary.main" fontWeight={700}>
                What it answers
              </Typography>
              <Typography variant="body2">{def.question}</Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="overline" color="primary.main" fontWeight={700}>
                How it's derived
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {def.derivation}
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="primary.main" fontWeight={700}>
                Pillar
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {def.pillar}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: theme.palette.action.hover,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {def.caveat}
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};
